import * as THREE from 'three';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader';

export class ModelLoader {
  private static instance: ModelLoader;
  private paragliderModel: THREE.Group | null = null;
  private modelLoaded: boolean = false;
  private modelLoadPromise: Promise<THREE.Group> | null = null;

  private constructor() {}

  static getInstance(): ModelLoader {
    if (!ModelLoader.instance) {
      ModelLoader.instance = new ModelLoader();
    }
    return ModelLoader.instance;
  }

  async loadParagliderModel(): Promise<THREE.Group> {
    if (this.modelLoadPromise) {
      return this.modelLoadPromise;
    }

    this.modelLoadPromise = new Promise((resolve, reject) => {
      const loader = new OBJLoader();

      // Try loading the OBJ file first
      loader.load(
        '/Paraglider_v_2.obj',
        (object) => {

          // Scale the model to appropriate size (very small for map icon)
          const scale = 0.0000000005; // Even smaller for map scale
          object.scale.set(scale, scale, scale);

          // Rotate model -90 degrees to stand upright, then flip 180 degrees around Z
          object.rotation.x = -Math.PI / 2;  // Stand upright
          object.rotation.z = Math.PI;  // Flip 180 degrees to be right-side up

          // Calculate bounding box after transformations
          object.updateMatrixWorld(true);
          const box = new THREE.Box3().setFromObject(object);
          box.getSize(new THREE.Vector3());

          // Remove Z offset - model will be centered at the red sphere
          object.position.z = 0;

          // Store the model for cloning
          this.paragliderModel = object;
          this.modelLoaded = true;

          resolve(object);
        },
        (progress) => {
        },
        (error) => {
          console.error('[ModelLoader] Error loading OBJ model:', error);

          // Fallback to FBX if OBJ fails
          const fbxLoader = new FBXLoader();
          fbxLoader.load(
            '/Paraglider_v_2.FBX',
            (fbxObject) => {

              // Scale the model
              const scale = 0.0000000005;
              fbxObject.scale.set(scale, scale, scale);

              // Rotate model -90 degrees to stand upright, then flip 180 degrees around Z
              fbxObject.rotation.x = -Math.PI / 2;
              fbxObject.rotation.z = Math.PI;

              // Calculate bounding box after transformations
              fbxObject.updateMatrixWorld(true);
              const fbxBox = new THREE.Box3().setFromObject(fbxObject);
              fbxBox.getSize(new THREE.Vector3());

              // Remove Z offset - model will be centered at the red sphere
              fbxObject.position.z = 0;

              this.paragliderModel = fbxObject;
              this.modelLoaded = true;

              resolve(fbxObject);
            },
            undefined,
            (fbxError) => {
              console.error('[ModelLoader] Error loading FBX model:', fbxError);
              reject(fbxError);
            }
          );
        }
      );
    });

    return this.modelLoadPromise;
  }

  getModel(): THREE.Group | null {
    return this.paragliderModel;
  }

  isLoaded(): boolean {
    return this.modelLoaded;
  }

  cloneModel(): THREE.Group | null {
    if (!this.paragliderModel) return null;
    return this.paragliderModel.clone();
  }

  applyColorToModel(model: THREE.Group, color: string): void {
    const threeColor = new THREE.Color(color);

    // Apply color to all meshes and lines in the model
    model.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        if (child.material) {
          const material = (child.material as THREE.Material).clone();
          if (material instanceof THREE.MeshPhongMaterial ||
              material instanceof THREE.MeshStandardMaterial ||
              material instanceof THREE.MeshBasicMaterial) {
            // Make the color brighter by increasing lightness
            const hsl = { h: 0, s: 0, l: 0 };
            threeColor.getHSL(hsl);
            const brightColor = new THREE.Color();
            brightColor.setHSL(hsl.h, Math.max(hsl.s, 0.9), Math.max(hsl.l, 0.6));

            material.color = brightColor;
            material.transparent = true;
            material.opacity = 0.95;

            // Add some emissive color for extra brightness
            if (material instanceof THREE.MeshPhongMaterial || material instanceof THREE.MeshStandardMaterial) {
              material.emissive = brightColor.clone().multiplyScalar(0.2);
            }
          }
          child.material = material;
        }
      } else if (child instanceof THREE.Line || child instanceof THREE.LineSegments) {
        // Handle lines (suspension wires) specially
        if (child.material) {
          const lineMaterial = new THREE.LineBasicMaterial({
            color: 0x444444, // Dark gray for wires
            linewidth: 3, // Thicker lines (note: may not work in all renderers)
            opacity: 1.0, // Fully opaque for visibility
            transparent: false
          });
          child.material = lineMaterial;

          // Also make the geometry thicker by converting to a tube if it's a simple line
          // This is more complex and might not be needed if the lines are visible enough
        }
      }
    });
  }
}